import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { ShoppingCart, Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from '../context/TranslationContext';
import { useSanityProduct } from '../hooks/useSanityProducts';
import SEO from '../components/SEO';
import { PAGE_SEO } from '../data/seoMeta';

// Helper to convert YouTube URL to embed URL
function getYouTubeEmbedUrl(url) {
  if (!url) return null;
  var shortsMatch = url.match(/youtube\.com\/shorts\/([a-zA-Z0-9_-]+)/);
  if (shortsMatch) return 'https://www.youtube.com/embed/' + shortsMatch[1] + '?autoplay=1&mute=1';
  var shortMatch = url.match(/youtu\.be\/([a-zA-Z0-9_-]+)/);
  if (shortMatch) return 'https://www.youtube.com/embed/' + shortMatch[1] + '?autoplay=1&mute=1';
  var watchMatch = url.match(/youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)/);
  if (watchMatch) return 'https://www.youtube.com/embed/' + watchMatch[1] + '?autoplay=1&mute=1';
  return url;
}

function MuteClipPage() {
  var { language, formatPrice, t } = useTranslation();
  var { product: sanityProduct } = useSanityProduct('muteclip');
  var [activeIdx, setActiveIdx] = useState(0);
  var navigate = useNavigate();

  var product = {
    name: 'MuteClip',
    price: 11.99,
    originalPrice: 14.99,
    packPrice: 34.99,
    image: 'https://static.wixstatic.com/media/4a2ebb_46a74401802747469ac90c062bec6f52~mv2.jpg',
  };

  function handleBuyNow() { navigate('/product/muteclip'); }

  // Pull gallery from Sanity (live updates when you re-order or add images)
  var galleryImages = [];
  if (sanityProduct) {
    if (Array.isArray(sanityProduct.images) && sanityProduct.images.length > 0) {
      galleryImages = sanityProduct.images.slice();
    } else if (sanityProduct.image) {
      galleryImages = [sanityProduct.image];
    }
  }
  if (galleryImages.length === 0) galleryImages = [product.image];

  // Get language-specific video
  var videoUrl = null;
  if (sanityProduct && sanityProduct.videosByLanguage) {
    videoUrl = sanityProduct.videosByLanguage[language] || sanityProduct.videosByLanguage['en'];
  }
  var embedUrl = getYouTubeEmbedUrl(videoUrl);

  var features = [
    t('muteClipFeature1'),
    t('muteClipFeature2'),
    t('muteClipFeature3'),
    t('muteClipFeature4'),
    t('muteClipFeature5'),
    t('muteClipFeature6')
  ];

  // Auto-rotate gallery every 4 seconds. Resets when activeIdx is manually changed
  // via the prev/next/dot buttons (the dependency on galleryImages.length is intentional
  // — if you re-order/add images in Sanity, the interval picks up the new length).
  useEffect(function() {
    if (galleryImages.length < 2) return undefined;
    var id = setInterval(function() {
      setActiveIdx(function(prev) { return (prev + 1) % galleryImages.length; });
    }, 4000);
    return function() { clearInterval(id); };
  }, [galleryImages.length]);

  function goPrev() { setActiveIdx((activeIdx - 1 + galleryImages.length) % galleryImages.length); }
  function goNext() { setActiveIdx((activeIdx + 1) % galleryImages.length); }

  return (
    <Layout>
      <SEO title={PAGE_SEO['muteclip'].title} description={PAGE_SEO['muteclip'].description} url="/muteclip" />
      <div className="pt-32 pb-20 bg-black min-h-screen">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="text-[#d4af37] text-sm tracking-widest uppercase mb-4">{t('innovation')}</p>
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">MuteClip</h1>
            <p className="text-xl text-neutral-400">
              {t('muteClipTagline')}
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-y-8 lg:gap-16 items-start mb-20">
            <div className="space-y-6">
              {embedUrl ? (
                <iframe
                  src={embedUrl}
                  title="MuteClip Video"
                  className="w-full aspect-video rounded-2xl bg-neutral-900"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : null}
              {/* Single-image rotating carousel */}
              <div className="relative">
                <div className="relative w-full aspect-square rounded-2xl overflow-hidden bg-neutral-900 border border-white/5">
                  {galleryImages.map(function(src, idx) {
                    return (
                      <img
                        key={idx}
                        src={src}
                        alt={'MuteClip view ' + (idx + 1)}
                        // Inline style avoids the Tailwind-JIT pitfall: when
                        // opacity classes are built from a runtime ternary
                        // ("opacity-100" / "opacity-0") the compiler can't
                        // detect them and they get purged from the bundle,
                        // leaving the carousel frozen on image #1 with only
                        // the dots updating. Inline opacity always wins.
                        className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
                        style={{ opacity: idx === activeIdx ? 1 : 0 }}
                        referrerPolicy="no-referrer"
                        loading={idx > 0 ? 'lazy' : undefined}
                      />
                    );
                  })}
                  {galleryImages.length > 1 ? (
                    <>
                      <button
                        type="button"
                        onClick={goPrev}
                        aria-label="Previous image"
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center backdrop-blur-sm transition-colors"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      <button
                        type="button"
                        onClick={goNext}
                        aria-label="Next image"
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center backdrop-blur-sm transition-colors"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                    </>
                  ) : null}
                </div>
                {galleryImages.length > 1 ? (
                  <div className="flex justify-center gap-2 mt-4">
                    {galleryImages.map(function(_, idx) {
                      return (
                        <button
                          key={idx}
                          type="button"
                          onClick={function() { setActiveIdx(idx); }}
                          aria-label={'Go to image ' + (idx + 1)}
                          className={'h-1.5 rounded-full transition-all ' + (idx === activeIdx ? 'w-8 bg-[#d4af37]' : 'w-1.5 bg-white/20 hover:bg-white/40')}
                        />
                      );
                    })}
                  </div>
                ) : null}
              </div>
            </div>

            <div>
              <p className="text-neutral-300 text-lg leading-relaxed mb-8">
                {t('muteClipDescription1')}
              </p>
              <p className="text-neutral-300 text-lg leading-relaxed mb-8">
                {t('muteClipDescription2')}
              </p>

              <div className="mb-8">
                <h3 className="text-lg font-semibold text-white mb-4">{t('features')}</h3>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {features.map(function(feature, index) {
                    return (
                      <li key={index} className="flex items-center gap-3 text-neutral-300">
                        <span className="w-6 h-6 rounded-full bg-[#d4af37]/20 flex items-center justify-center flex-shrink-0">
                          <Check className="w-4 h-4 text-[#d4af37]" />
                        </span>
                        {feature}
                      </li>
                    );
                  })}
                </ul>
              </div>

              <div className="bg-neutral-900 border border-white/10 rounded-2xl p-6 mb-8">
                <div className="flex items-baseline gap-4 mb-2">
                  <span className="text-neutral-400">{t('muteClipSingle')}:</span>
                  <span className="text-3xl font-bold text-[#d4af37]">{formatPrice(product.price)}</span>
                  <span className="text-lg text-neutral-600 line-through">{formatPrice(product.originalPrice)}</span>
                </div>
                <div className="flex items-baseline gap-4">
                  <span className="text-neutral-400">{t('muteClipPack')}:</span>
                  <span className="text-3xl font-bold text-[#d4af37]">{formatPrice(product.packPrice)}</span>
                </div>
              </div>

              <button
                type="button"
                onClick={handleBuyNow}
                data-testid="muteclip-buy-btn"
                className="w-full flex items-center justify-center gap-3 bg-[#d4af37] text-black hover:bg-white rounded-full px-8 py-5 font-semibold text-lg transition-all duration-300"
              >
                <ShoppingCart className="w-6 h-6" />
                {t('buyNow')}
              </button>
            </div>
          </div>

          <div className="text-center">
            <Link to="/shop" className="text-[#d4af37] hover:underline text-lg">
              ← {t('checkOtherProducts')}
            </Link>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default MuteClipPage;
